import { FRONTEND_DESIGN_SKILL, skillFiles } from "../skills";
import { workflowSkillFiles } from "../skills/workflowSkillFiles";
import { templateStepFiles } from "../templateSteps/files";
import type { CodeTemplate } from "../templateSteps/index";
import { workflowReference } from "./workflowReference";

export type AgentFilesOptions = {
  uiUrl: string;
  apiUrl: string;
  frontendDesignEnabled?: boolean;
};

const TEMPLATES_DIR = ".3b/templates";
const SKILLS_DIR = ".3b/skills";

/**
 * AGENTS.md content shipped with cloned/downloaded workflow repos. It mostly
 * delegates to the shared workflowReference, then adds git/CLI/REST-API
 * sections that only apply outside the in-app agent.
 */
function workflowAgentsMd({
  uiUrl,
  apiUrl,
  frontendDesignEnabled = true,
}: AgentFilesOptions): string {
  return `# Working with this workflow

This is a [3B](${uiUrl}) workflow. 3B is a workflow automation platform where you build workflows from linked steps. Each step's stdout flows to linked downstream steps via stdin.

${workflowReference({ frontendDesignEnabled })}

## Step templates (local)

Read-only template scaffolds are available at \`${TEMPLATES_DIR}/<type>/\` (shell, python, typescript, react, agent). Always create a new step by copying a template:

\`\`\`sh
cp -r ${TEMPLATES_DIR}/typescript "<Name>"
\`\`\`

Default to typescript for data processing.

For an agent that calls a model — chat or autonomous — copy the \`agent\` template instead of hand-rolling a model loop. It ships a working loop (streaming, transcript, usage, run limits) as editable files in \`agent.ts\` and \`runtime/\`; you normally only touch \`system.md\`, \`tools/\` (one tool per file), \`model.json\`, \`config.toml\`, and \`buildInput\` in \`agent.ts\` — the function you own that shapes the trigger payload into the agent's input. A \`route\` plus \`output = true\` in \`config.toml\` makes it caller-invoked (chat or webhook); without them it is a headless node — the previous step's output is its input, and its whole stdout is one structured result object whose \`text\` field is the final answer. Read the building-agents skill before building one: it covers routing, auth, conversations, tools, and the mandatory self-loop for async agents (one LLM request per run, linked back to itself).

## Skills (local)

Reference material for specific capabilities is available at \`${SKILLS_DIR}/<name>/SKILL.md\`. Read a skill’s \`SKILL.md\` when you need detailed guidance on the topic it covers — for example, before writing a React step or building an agentic loop. A skill may also include referenced files under its directory; resolve those paths relative to the skill’s \`SKILL.md\`.

## Git

If this directory contains a \`.git\` folder, it is a clone of a 3B space; each top-level directory is one workflow. You can use standard git operations to make changes:

\`\`\`sh
# Edit files, then commit and push
git add -A && git commit -m "Describe your change" && git push
\`\`\`

Changes pushed to the remote are applied to the workflow in 3B. If someone edited the workflow in the cloud while you were working locally, git will reject your push — pull first, resolve any conflicts, then push again.

The \`AGENTS.md\` file and the \`.3b/\` directory are managed by 3B and will be regenerated automatically. Don't check them in or modify them.

## Installing the 3B CLI

\`\`\`sh
curl -fsSL ${uiUrl}/cli/install.sh | sh
\`\`\`

Then configure your API key:

\`\`\`sh
3b configure
\`\`\`

run \`3b --help\` for more information.

### Examples

\`\`\`sh
# Run a workflow with input and wait for output
echo "Hello" | 3b run wf_abc123 --wait

# Run with JSON input
cat data.json | 3b run wf_abc123 --wait --type application/json

# Chat with a workflow
3b chat wf_abc123

# One-shot chat from stdin
echo "Summarize this" | 3b chat wf_abc123
\`\`\`

## API

The 3B REST API is documented at ${apiUrl}/api/v1/docs. It is unstable and likely to change: breaking changes can ship at any time, so treat anything you build on it as provisional. Authenticate with \`Authorization: Bearer <api-key>\`. Key endpoints:

- \`GET /api/v1/spaces\` — list spaces
- \`GET /api/v1/spaces/:id/workflows\` — list workflows
- \`POST /api/v1/workflows/:id/run\` — trigger a run
- \`GET /api/v1/workflows/:id/runs/:runId\` — check status
- \`POST /api/v1/workflows/:id/chat\` — chat with a workflow
`;
}

function templateFiles(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [template, tmpl] of Object.entries(templateStepFiles) as [
    CodeTemplate,
    (typeof templateStepFiles)[CodeTemplate],
  ][]) {
    for (const file of tmpl.files) {
      out[`${TEMPLATES_DIR}/${template}/${file.path}`] = file.content;
    }
  }
  return out;
}

/**
 * Files injected by 3B into cloned/downloaded workflow repos, not stored in
 * the DB. Keyed by path (top-level files at the repo root, templates and
 * skills under \`.3b/\`).
 */
export function workflowAgentFiles(
  opts: AgentFilesOptions
): Record<string, string> {
  const skills =
    opts.frontendDesignEnabled === false
      ? Object.fromEntries(
          Object.entries(skillFiles).filter(
            ([path]) => !path.startsWith(`${FRONTEND_DESIGN_SKILL}/`)
          )
        )
      : skillFiles;
  return {
    "AGENTS.md": workflowAgentsMd(opts),
    "CLAUDE.md": "@AGENTS.md\n",
    ...templateFiles(),
    ...workflowSkillFiles(skills),
  };
}

/**
 * Top-level managed file names. Used by the UI workflow zip import to skip
 * these specific names — paths under `.3b/` are skipped automatically by
 * the dot-prefix rule.
 */
export const workflowAgentFileNames = ["AGENTS.md", "CLAUDE.md"] as const;
